import type { Route } from 'vue-router';
import type { Context } from '@/@types/ssr';
import { getCurrentInstance } from 'vue';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { headerfulCachedRequest } from '@/helpers/request-helper';
import {
	GetUrlMetadataDocument,
	GetUrlMetadataQuery,
	GetUrlMetadataQueryVariables,
} from '@/graphql/query/GetUrlMetadata.query';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';

/**
 * Supported domains/subdomains
 * This is also where we declare what the subdomain name actually is, e.g. guides.justwatch.com or sports.justwatch.com
 */
export const Domain = {
	MAIN: 'www',
	EDITORIAL: 'guides',
	SPORTS: 'sports',
} as const;

export type Domain = (typeof Domain)[keyof typeof Domain];

// BR_SUBDOMAIN
/**
 * Brazil is served at its own public hostname (br.<domain>) while remaining `www`-scoped content
 * under the hood (same routes, same `site` header/GraphQL scoping). It's deliberately NOT part of
 * the `Domain` enum above since it doesn't participate in routing-tree or content-scoping
 * decisions - it only matters for building absolute URLs (canonical, hreflang, analytics).
 */
export const BR_HOST = `br.${JW_CONFIG.DOMAIN}`;

/**
 * Whether a hostname (with or without port) is the BR subdomain - either the real production/
 * stage host (`BR_HOST`) or the local-dev equivalent (`br.localhost`), mirroring how
 * `getSubdomain`/`detectDomain` below special-case `*.localhost` hostnames.
 */
export function isBrHost(host: string): boolean {
	const withoutPort = (host || '').split(':')[0];
	return withoutPort === BR_HOST || withoutPort === 'br.localhost';
}

/** Whether a (normalized, leading-slash) path is one of the `/br/*` main-domain routes now also
 * reachable at BR_HOST. Ignores any query string/hash (e.g. pagination's `/br?page=2`). */
function isBrPath(path: string): boolean {
	const pathOnly = path.split('?')[0].split('#')[0];
	return pathOnly === '/br' || pathOnly.startsWith('/br/');
}
// BR_SUBDOMAIN

/**
 * Extracts subdomain from a hostname or site header value
 * @param input - The hostname to parse (e.g., 'guides.justwatch.com') or site header value (e.g., 'guides', 'www')
 * @returns The subdomain part or null if no subdomain
 */
export function getSubdomain(input: string): Domain | null {
	if (!input) return null;

	// Remove port if present (e.g., 'guides.localhost:8080' -> 'guides.localhost')
	const inputWithoutPort = input.split(':')[0];

	// Check if this is a simple site header value (no dots)
	if (!inputWithoutPort.includes('.')) {
		// Direct site header values like 'guides', 'www', 'sports'
		// Return null for 'www' as it represents the main domain
		return inputWithoutPort === 'www' ? null : (inputWithoutPort as Domain);
	}

	// Handle hostname formats with dots
	const parts = inputWithoutPort.split('.');

	// Handle .localhost domains (e.g., 'guides.localhost')
	if (parts.length === 2 && parts[1] === 'localhost') {
		return parts[0] === 'localhost' ? null : (parts[0] as Domain);
	}

	// Handle regular domains (e.g., 'guides.justwatch.com')
	if (parts.length <= 2) {
		return null;
	}

	// Return first part as subdomain, but return null for 'www'
	const subdomain = parts[0];
	return subdomain === 'www' ? null : (subdomain as Domain);
}

/**
 * Detects which domain the current input belongs to
 * @param input - The hostname (e.g., 'guides.justwatch.com', 'guides.localhost:8080') or site header value (e.g., 'guides', 'www') to check
 * @returns The domain type
 */
export function detectDomain(input: string): Domain {
	// Handle empty or invalid input
	if (!input || typeof input !== 'string') return Domain.MAIN;

	const subdomain = getSubdomain(input);

	// Map subdomains to domain types - easily extensible for new subdomains
	if (!subdomain) {
		return Domain.MAIN;
	}

	// Check if the subdomain matches any known domain values
	const domainValues = Object.values(Domain) as Domain[];
	for (const domainValue of domainValues) {
		if (subdomain === domainValue) {
			return domainValue;
		}
	}

	// Default to main domain for unknown subdomains
	return Domain.MAIN;
}

/**
 * Gets the appropriate domain for a given route based on its meta
 */
export function getDomainForRoute(routeMeta: any): Domain {
	if (!routeMeta) return Domain.MAIN;

	// Check if route explicitly specifies a subdomain in its meta
	if (routeMeta.subdomain && routeMeta.subdomain !== Domain.MAIN) {
		return routeMeta.subdomain;
	}

	// Default to main domain for all routes unless explicitly marked
	return Domain.MAIN;
}

/**
 * Get current location info for URL building
 * Works consistently across client and server environments
 */
function getCurrentLocation(): string | { protocol: string; host: string } {
	if (process.client && window.location) {
		// Use window.location for better URL handling on client
		return window.location;
	} else {
		// During SSR, get location from Vue context if available
		const context = getCurrentInstance()?.proxy?.$context;
		if (context?.req) {
			const host = context.req.get('host') || context.req.hostname || '';
			// Force HTTPS for production domains, allow original protocol for localhost
			const protocol = host.includes('localhost')
				? context.req.protocol
					? `${context.req.protocol}:`
					: 'http:'
				: 'https:';
			return { protocol, host };
		}
	}
	captureMessageForSentry(
		'subdomain-helper: Could not find location on client via window.location or during SSR from Vue context.',
		{
			where: '[subdomain-helper.ts] getCurrentLocation()',
		},
		'warning'
	);

	// Fallback if no location info is available
	return `https://www.${JW_CONFIG.DOMAIN}`;
}

/**
 * Builds the full URL for a route on the appropriate domain
 * Uses URL API for more robust URL handling
 */
export function buildUrlForDomain(
	path: string,
	targetDomain: Domain,
	// BR_SUBDOMAIN
	options?: { forceBrHost?: boolean }
	// BR_SUBDOMAIN
): string {
	// Handle different input types - string or location-like object
	let protocol = 'https:';
	let currentHost = '';

	const currentLocation = getCurrentLocation();

	if (typeof currentLocation === 'string') {
		// If a string is provided, it's assumed to be the hostname or full URL
		if (currentLocation.includes('://')) {
			// It's a full URL, parse it
			try {
				const url = new URL(currentLocation);
				// Force HTTPS for production domains, allow original protocol for localhost
				protocol = url.hostname.includes('localhost') ? url.protocol : 'https:';
				currentHost = url.host; // includes port if present
			} catch (e) {
				// In case parsing fails, fall back to treating it as a hostname
				currentHost = currentLocation;
			}
		} else {
			currentHost = currentLocation;
		}
	} else {
		// It's a location-like object
		// Force HTTPS for production domains, allow original protocol for localhost
		if (currentLocation.host && !currentLocation.host.includes('localhost')) {
			protocol = 'https:';
		} else {
			protocol = currentLocation.protocol || protocol;
		}
		currentHost = currentLocation.host;
	}

	// Get the current subdomain
	const currentSubdomain = getSubdomain(currentHost);

	// Prepare the base URL
	let baseUrl: string;

	// BR_SUBDOMAIN
	const normalizedPath = path.startsWith('/') ? path : `/${path}`;
	// /br/* routes landing back on the main domain belong at BR_HOST, not www - e.g. navigating
	// from guides.* or sports.* back to a /br route should return to br.<domain>, not www.<domain>.
	// `options.forceBrHost` covers the same "land on BR_HOST" outcome for paths that can't carry
	// a /br prefix themselves - namely app.home, which is always path '/' (prefixing it would
	// resolve to the popular-list route instead of home).
	const isBrMainRoute = targetDomain === Domain.MAIN && (isBrPath(normalizedPath) || !!options?.forceBrHost);
	// BR_SUBDOMAIN

	// For localhost, handle specially
	if (currentHost.includes('localhost')) {
		// Remove any existing subdomain
		const localhostBase = currentSubdomain ? currentHost.replace(`${currentSubdomain}.`, '') : currentHost;

		// Apply the target subdomain
		// BR_SUBDOMAIN
		const subdomain = isBrMainRoute ? 'br' : targetDomain === Domain.MAIN ? '' : targetDomain;
		// BR_SUBDOMAIN
		baseUrl = `${protocol}//${subdomain ? `${subdomain}.` : ''}${localhostBase}`;
		// BR_SUBDOMAIN
	} else if (isBrMainRoute) {
		baseUrl = `${protocol}//${BR_HOST}`;
		// BR_SUBDOMAIN
	} else {
		// For production domains, extract the base domain (e.g., justwatch.com)
		const baseDomain = JW_CONFIG.DOMAIN; // e.g., justwatch.com

		// Apply the target subdomain
		const subdomain = targetDomain === Domain.MAIN ? 'www' : targetDomain;
		baseUrl = `${protocol}//${subdomain}.${baseDomain}`;
	}

	// Combine with the path, ensuring proper slash handling
	return `${baseUrl}${normalizedPath}`;
}

async function isValidPath(targetPath: string, targetDomain: Domain, jwId: string | undefined) {
	try {
		const variables: GetUrlMetadataQueryVariables = { fullPath: targetPath, site: targetDomain };
		const response: { data: GetUrlMetadataQuery } = await headerfulCachedRequest(
			GetUrlMetadataDocument,
			variables,
			jwId
		);
		return !!response?.data?.urlV2;
	} catch (error: any) {
		// If the request fails (network error, 500, etc.), treat the path as invalid
		// This matches the previous behavior where headerfulCachedRequest returned undefined on error
		const { captureMessageForSentry } = await import('@/helpers/sentry-helper');
		captureMessageForSentry(
			'[subdomain-helper.ts] isValidPath request failed',
			{
				error,
				targetPath,
				targetDomain,
				where: 'subdomain-helper.ts isValidPath',
			},
			'warning'
		);
		return false;
	}
}

/**
 * Returns `true` when it performed a hard cross-domain redirect (`window.location.href`) - the
 * caller must skip the rest of the SPA navigation (and never call router's `next()`) in that
 * case, otherwise the SPA transition completes first and the URL flashes the wrong host/path
 * before the actual redirect takes over.
 */
export async function handleSubdomainSwitch(context: Context, from: Route, to: Route): Promise<boolean> {
	const currentDomain = detectDomain(window.location.hostname);
	const targetDomain = getDomainForRoute(to.meta);
	const userStore = useUserStore(context.pinia);
	const languageStore = useLanguageStore(context.pinia);
	const routingStore = useRoutingStore(context.pinia);
	const currentWebLocale = languageStore.webLocale;
	const jwId = userStore.jwId;

	// BR_SUBDOMAIN: br.<domain> and www.<domain> are both `Domain.MAIN` internally (same content
	// scoping, no dedicated route tree) - but they're different real hostnames. Landing directly on
	// a URL (`firstRouting`) is intentionally left to infra to redirect (no SPA is running yet to
	// intercept it); but a genuine in-app navigation - e.g. the country switcher moving from a br
	// locale to a non-br one, or vice versa - happens purely client-side, so nothing but this guard
	// can correct the host for it.
	const firstRouting = !from?.name || from.name === 'app.refreshtoken';
	const currentIsBr = isBrHost(window.location.hostname);
	// Home (path '/') is host-agnostic - it renders on whichever host you're already on (the
	// br-locale override lives in main.ssr.ts, not in the path), so it should never be treated as
	// "leaving"/"entering" br the way a /br-prefixed route is.
	const targetIsBr = to.name === 'app.home' ? currentIsBr : targetDomain === Domain.MAIN && isBrPath(to.fullPath);
	const isBrHostSwitch =
		!firstRouting && currentDomain === Domain.MAIN && targetDomain === Domain.MAIN && currentIsBr !== targetIsBr;
	// BR_SUBDOMAIN

	// Check if we need to switch domains
	if (currentDomain === targetDomain && !isBrHostSwitch) return false;

	// Special case: If we're on any subdomain and trying to access the root path (/),
	// and the route has matching subdomain metadata, this should be the subdomain landing page.
	// Don't redirect to main domain.
	if (currentDomain !== Domain.MAIN && to.path === '/' && to.meta?.subdomain === currentDomain) {
		return false;
	}

	// Don't redirect when going FROM main domain TO sports/editorial subdomains
	// This allows main domain fallback routes to work for backward compatibility
	// (bookmarks, old links, direct URL access)
	// Only redirect when going FROM sports/editorial TO main, or between subdomains
	if (currentDomain === Domain.MAIN && (targetDomain === Domain.SPORTS || targetDomain === Domain.EDITORIAL)) {
		return false;
	}

	// Don't redirect between non-main subdomains (e.g. guides.domain -> sports.domain).
	// This can happen when a locale supports sports but not editorial — the sports landing
	// route (path: '/') would match on guides.domain and incorrectly trigger a redirect.
	if (currentDomain !== Domain.MAIN && targetDomain !== Domain.MAIN) {
		return false;
	}

	let targetPath = '';

	// Handle navigation from any subdomain to main domain
	if (targetDomain === Domain.MAIN) {
		// Special handling for different route types
		if (to.name === 'app.home') {
			// Home page should go to root of main domain
			targetPath = '/';
		} else if (to.name === 'app.titles.popular.list') {
			// Popular page goes to locale root
			targetPath = `/${currentWebLocale}`;
		} else {
			// All other routes: ensure they have locale prefix
			targetPath = to.fullPath.startsWith(`/${currentWebLocale}`)
				? to.fullPath
				: `/${currentWebLocale}${to.fullPath}`;
		}
	}
	// Handle navigation from main domain or other subdomains to target subdomain
	else {
		targetPath = to.fullPath;
	}

	const shouldRedirect = await isValidPath(targetPath, targetDomain, jwId);

	if (!shouldRedirect) {
		// If the target path is not valid on the target domain, do not redirect
		const { captureMessageForSentry } = await import('@/helpers/sentry-helper');
		captureMessageForSentry(
			'Path Not found on Target Domain',
			{
				targetPath,
				targetDomain,
				where: 'subdomain-helper.ts',
			},
			'info'
		);
		return false;
	}

	routingStore.setActiveDomain(targetDomain);

	// Perform the domain switch
	const targetUrl = buildUrlForDomain(targetPath, targetDomain);
	window.location.href = targetUrl;
	return true; // Caller must skip next() - we're redirecting
}
